import React from 'react';

import {
  DateField,
  FunctionField,
  TextField,
  useRecordContext,
} from 'react-admin';

import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Box,
  Divider,
} from '@mui/material';

import { isEmpty, isString } from 'lodash';

import { Alpha3Code, getName } from 'i18n-iso-countries';

import { AdverseMediaMatches } from '../../../../../../utilities/schemas/adverseMediaMatches';
import languages from '../../../../../../constants/languages';
import AdverseMediaTitle from '../../../../../individual/editTabs/kyc/adverseMedia/details/AdverseMediaTitle';

const getAddress = ({
  streetAddress,
  additionalAddress,
  city,
  country,
  description,
  postcode,
  state,
}: {
  streetAddress?: string;
  additionalAddress?: string;
  postcode?: string;
  city?: string;
  state?: string;
  country?: Alpha3Code;
  description?: string;
}) => {
  if (description && !isEmpty(description)) {
    return description;
  }

  const address = [
    streetAddress,
    additionalAddress,
    postcode,
    city,
    state,
    country ? getName(country, languages.EN) : null,
  ].filter((item) => item && isString(item) && !isEmpty(item));

  return address.join(', ');
};

const AdverseMediaAboutBusiness = () => {
  const record = useRecordContext<AdverseMediaMatches['potentialMatches'][number]>();

  if (!record) {
    return null;
  }

  return (
    <Grid size={12}>
      <Card variant="outlined" sx={{ margin: 0 }}>
        <CardHeader title="About" />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Box>
                <AdverseMediaTitle>Name</AdverseMediaTitle>
                <TextField source="fullName" emptyText="-" />
              </Box>
            </Grid>
            <Grid size={12}>
              <Box>
                <AdverseMediaTitle>Registration number</AdverseMediaTitle>
                <DateField source="registrationNumber" emptyText="-" />
              </Box>
            </Grid>
            <Grid size={12}>
              <Box>
                <AdverseMediaTitle>Registration country</AdverseMediaTitle>
                <DateField source="registrationCountry" emptyText="-" />
              </Box>
            </Grid>
            <Grid size={12}>
              <Box>
                <AdverseMediaTitle>Other names</AdverseMediaTitle>
                <FunctionField
                  sx={{ whiteSpace: 'pre' }}
                  source="otherNames"
                  render={(item: AdverseMediaMatches['potentialMatches'][number]) => {
                    if (isEmpty(item.otherNames)) return '-';
                    return record.otherNames
                      .map((name) => name.fullName)
                      .join('\n');
                  }}
                />
              </Box>
            </Grid>
            <Grid size={12}>
              <Box>
                <AdverseMediaTitle>Address</AdverseMediaTitle>
                <FunctionField
                  sx={{ whiteSpace: 'pre' }}
                  source="address"
                  render={(item: AdverseMediaMatches['potentialMatches'][number]) => {
                    if (!item.address) return '-';
                    return getAddress(item.address);
                  }}
                />
              </Box>
            </Grid>
            <Grid size={12}>
              <Box>
                <AdverseMediaTitle>Other addresses</AdverseMediaTitle>
                <FunctionField
                  sx={{ whiteSpace: 'pre' }}
                  source="otherAddresses"
                  render={(item: AdverseMediaMatches['potentialMatches'][number]) => {
                    if (isEmpty(item.otherAddresses)) return '-';
                    return record.otherAddresses
                      .map((otherAddress) => getAddress(otherAddress))
                      .join('\n');
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default AdverseMediaAboutBusiness;
